import { Add, Close, Upload } from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  ListItem,
  ListItemText,
  TextField,
  Typography,
} from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { LoadingButton } from "@mui/lab";
import { AuthContext } from "../../../context/AuthContext";
import { SnackAlertContext } from "../../../context/SnackAlertContext";
import useFileUpload from "../../../hooks/useFileUpload";
import S3PresignedPdf from "../../../components/S3PresignedPdf";
import S3PresignedImage from "../../../components/S3PresignedImage";
import EmployeeDetails from "./EmployeeDetails";

const UploadSmsConsentDialog = ({
  employeeId,
  phone,
  open,
  onClose,
  onSuccess,
  // From Manual Upload
  downloadName,
}) => {
  const { authToken, ezorder } = useContext(AuthContext);
  const { openSnackMessage } = useContext(SnackAlertContext);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const { uploadFile } = useFileUpload(ezorder, authToken);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedFileS3Key, setUploadedFileS3Key] = useState(null);

  const handleFileUpload = async (e) => {
    try {
      setIsUploading(true);
      const file = e.target.files[0];
      let identifier = `SHOP-EMPLOYEE-${employeeId}`;
      const key = await uploadFile(identifier, "employee-documents", file);
      console.log("File Uploaded to", key);
      setUploadedFileS3Key(key);
    } catch (error) {
      console.log(error);
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.log(error.response.data);
        console.log(error.response.status);
        console.log(error.response.headers);
        setErrorMessage(error.response.data.error);
      } else {
        // Something went really bad
        console.log(error);
        setErrorMessage(error);
      }
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    // Reset and close
    setUploadedFileS3Key(null);
    setErrorMessage("");
    onClose();
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      let data = null;
      if (uploadedFileS3Key) {
        data = {
          signedSmsConsentForm: uploadedFileS3Key,
          phone: phone,
        };
      }

      const response = await ezorder.patch(
        `/users/${employeeId}/sms-consent`,
        { signedSmsConsentForm: uploadedFileS3Key, phone: phone },
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (response.status == 200 || response.status == 201) {
        openSnackMessage(
          "success",
          "User subscribed! A new pin number was sent to this phone number"
        );
        onSuccess();
        handleClose();
      } else {
        console.log(response);
        setErrorMessage(response.response.data.error);
      }
    } catch (error) {
      console.log(error);
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.log(error.response.data);
        console.log(error.response.status);
        console.log(error.response.headers);
        setErrorMessage(error.response.data.error);
      } else {
        // Something went really bad
        console.log(error);
        setErrorMessage(error);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      fullWidth
      maxWidth="lg"
      open={open}
      onClose={() => {
        if (!loading) {
          handleClose();
        }
      }}
    >
      <DialogTitle>Upload Signed SMS Service Consent Form</DialogTitle>
      <DialogContent>
        <DialogContentText>Upload signed form for {phone}</DialogContentText>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {!uploadedFileS3Key ? (
            <>
              <LoadingButton
                loading={isUploading}
                component="label"
                variant="outlined"
                sx={{
                  width: "100%",
                  minHeight: "300px",
                  // padding: 1,
                  // display: "flex",
                  // flexDirection: "column",
                  // alignItems: "center",
                  // justifyContent: "center",
                }}
              >
                <Upload />
                Signed Form
                <input
                  hidden
                  accept="image/*,.doc,.docx,.pdf"
                  type="file"
                  onChange={handleFileUpload}
                />
              </LoadingButton>
            </>
          ) : (
            <>
              {uploadedFileS3Key && (
                <>
                  {uploadedFileS3Key.split(".")[1] === "pdf" ? (
                    <S3PresignedPdf
                      s3Key={uploadedFileS3Key}
                      downloadName={downloadName}
                    />
                  ) : (
                    <S3PresignedImage
                      s3Key={uploadedFileS3Key}
                      maxWidth={600}
                      minWidth={600}
                      height={600}
                    />
                  )}
                </>
              )}
            </>
          )}
        </Box>
      </DialogContent>
      <Box
        sx={{
          m: 1,
          position: "relative",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {errorMessage && (
          <Box>
            <Typography color={"red"}>{errorMessage}</Typography>
          </Box>
        )}
      </Box>
      <DialogActions>
        <LoadingButton
          loading={loading}
          onClick={() => {
            if (!loading) handleClose();
          }}
        >
          Cancel
        </LoadingButton>
        <LoadingButton
          disabled={!uploadedFileS3Key}
          loading={loading}
          variant="contained"
          color="primary"
          onClick={handleSubmit}
        >
          Submit
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default UploadSmsConsentDialog;
